import {
  getContractDetailAllInfoByConNum,
  getContractProductInfoByContractId,
  getReceiptAddressByContractId,
  requestExportComplianceDetection,
  requestExportComplianceReviewCreation
} from '@/API';

/**
 * 执行出口合规前置检测，并根据风险提示处理审查单创建交互。
 *
 * @param {Vue} vm 当前 Vue 组件实例
 * @param {Object} request 通用合规检测请求
 * @returns {Promise<{allowBusiness: boolean}>}
 */
export async function checkExportCompliance (vm, request) {
  let detectResponse;
  try {
    detectResponse = await requestExportComplianceDetection(request);
  } catch (error) {
    vm.$message.error('出口合规检测失败，请稍后重试');
    return { allowBusiness: false };
  }
  const responseBody = detectResponse.data || {};
  if (responseBody.status !== '200' || !responseBody.data) {
    vm.$message.error(responseBody.err || '出口合规检测失败，请稍后重试');
    return { allowBusiness: false };
  }

  const result = responseBody.data;
  const riskLevel = Number(result.overallRiskLevel);
  if (riskLevel <= 2) {
    return { ...result, allowBusiness: true };
  }

  if (result.reviewInfo && result.reviewInfo.hasReview) {
    // 法务已放行（审查单通过）：静默通过合规校验，不再弹窗
    if (isSilentlyAllowed(result, result.reviewInfo)) {
      return { ...result, allowBusiness: true };
    }
    await showReviewResult(vm, result, result.reviewInfo);
    return buildDecision(result, result.reviewInfo);
  }

  const hasReference = Boolean(result.detectionId || result.logId || result.engineLogId);
  const riskMessage = createMessageContent(vm, result) || resolveRiskMessage(result);
  const messageType = riskLevel === 2 ? 'warning' : 'error';
  if (!hasReference) {
    await vm.$alert(riskMessage || '检测到出口合规风险，但未获取到检测凭证，无法创建审查单', '出口合规提示', {
      type: messageType,
      customClass: 'compliance-message-box'
    });
    return buildDecision(result);
  }

  const confirmed = await confirmReviewCreation(
    vm,
    riskMessage,
    messageType
  );
  if (confirmed) {
    let reviewResponse;
    try {
      reviewResponse = await requestExportComplianceReviewCreation({
        businessScene: request.businessScene,
        originalBizId: request.originalBizId,
        detectionId: result.detectionId,
        engineLogId: result.logId || result.engineLogId
      });
    } catch (error) {
      vm.$message.error('出口合规审查单创建失败，请稍后重试');
      return buildDecision(result);
    }
    const reviewBody = reviewResponse.data || {};
    if (reviewBody.status !== '200' || !reviewBody.data) {
      vm.$message.error(reviewBody.err || '出口合规审查单创建失败，请稍后重试');
      return buildDecision(result);
    }
    notifyReviewSubmitted(vm, reviewBody.data);
    return { ...result, allowBusiness: false };
  }
  return buildDecision(result);
}

/**
 * 国际场景出口合规前置检测：仅 3/4 级弹窗并支持发起审查单；0/1/2 级直接放行。
 *
 * @param {Vue} vm 当前 Vue 组件实例
 * @param {Object} request 国际合规检测请求
 * @returns {Promise<{allowBusiness: boolean}>}
 */
export async function checkIntlExportCompliance (vm, request) {
  let detectResponse;
  try {
    detectResponse = await requestExportComplianceDetection(request);
  } catch (error) {
    vm.$message.error('出口合规检测失败，请稍后重试');
    return { allowBusiness: false };
  }
  const responseBody = detectResponse.data || {};
  if (responseBody.status !== '200' || !responseBody.data) {
    vm.$message.error(responseBody.err || '出口合规检测失败，请稍后重试');
    return { allowBusiness: false };
  }

  const result = responseBody.data;
  // 3 天内重复提交：平台返回已有检测结果；已创建审查单一并返回，直接展示状态
  if (result.reviewInfo && result.reviewInfo.hasReview) {
    // 法务已放行（审查单通过）或整体风险低于 2 级：静默通过合规校验，不再弹窗
    if (isSilentlyAllowed(result, result.reviewInfo)) {
      return { ...result, allowBusiness: true };
    }
    await showReviewResult(vm, result, result.reviewInfo);
    return { ...result, allowBusiness: result.reviewInfo.handleResult === 1 };
  }

  // 0/1/2 级直接放行；仅 3/4 级弹窗
  if (result.overallRiskLevel < 3) {
    return { ...result, allowBusiness: true };
  }

  const hasReference = Boolean(result.detectionId || result.logId || result.engineLogId);
  const riskMessage = createMessageContent(vm, result) || resolveRiskMessage(result);
  if (!hasReference) {
    // 无检测凭证无法创建审查单，仅提示并阻断
    await vm.$alert(riskMessage || '检测到出口合规高风险，请联系合规团队处理', '出口合规提示', {
      type: 'error',
      customClass: 'compliance-message-box'
    });
    return { ...result, allowBusiness: false };
  }

  const confirmed = await confirmReviewCreation(vm, riskMessage);
  if (!confirmed) {
    return { ...result, allowBusiness: false };
  }
  let reviewResponse;
  try {
    reviewResponse = await requestExportComplianceReviewCreation({
      businessScene: request.businessScene,
      originalBizId: request.originalBizId,
      detectionId: result.detectionId,
      engineLogId: result.logId || result.engineLogId
    });
  } catch (error) {
    vm.$message.error('出口合规审查单创建失败，请稍后重试');
    return { ...result, allowBusiness: false };
  }
  const reviewBody = reviewResponse.data || {};
  if (reviewBody.status !== '200' || !reviewBody.data) {
    vm.$message.error(reviewBody.err || '出口合规审查单创建失败，请稍后重试');
    return { ...result, allowBusiness: false };
  }
  notifyReviewSubmitted(vm, reviewBody.data);
  return { ...result, allowBusiness: false };
}

/** 审查单已通过（法务放行）或整体风险低于 2 级时静默放行：不再弹窗，直接通过合规校验。 */
function isSilentlyAllowed (result, reviewInfo) {
  if (reviewInfo && reviewInfo.handleResult === 1) {
    return true;
  }
  return result.overallRiskLevel != null && Number(result.overallRiskLevel) < 2;
}

/** 用户取消创建时，2 级继续提交，3/4 级保持拦截，并保留合规接口全部字段。 */
function buildDecision (result, reviewInfo) {
  return {
    ...result,
    allowBusiness: reviewInfo ? reviewInfo.handleResult === 1 : result.overallRiskLevel < 3
  };
}

async function showReviewResult (vm, result, reviewInfo) {
  const description = reviewInfo.handleResultDesc || reviewInfo.handleStatusDesc || '审查单已创建，等待处理';
  const reviewNo = reviewInfo.interceptNo ? `（${reviewInfo.interceptNo}）` : '';
  const reviewMessage = `该请求已存在合规审查单${reviewNo}，当前状态：${description}。`;
  try {
    await vm.$alert(createMessageContent(vm, result, [reviewMessage]), '合规审查提示', {
      type: reviewInfo.handleResult === 1 ? 'success' : 'warning',
      customClass: 'compliance-message-box'
    });
  } catch (error) {
    // 关闭提示不改变审查结果对应的业务放行结论。
  }
}

function resolveRiskMessage (result) {
  const detailMessage = (result.details || []).find(item => item.riskOutput);
  return resolveRiskMessages(result).join('\n') || result.prompt ||
    (detailMessage && detailMessage.riskOutput);
}

function resolveRiskMessages (result) {
  return Array.from(new Set([
    result.overallConclusion,
    result.resultMsg
  ].filter(message => message && String(message).trim()).map(message => String(message).trim())))
    .map(stripSubmitReviewQuestion)
    .filter(message => message);
}

/** 去掉平台提示语中“询问是否提交审核”的问句：弹窗按钮已表达该动作，无需重复展示。 */
function stripSubmitReviewQuestion (message) {
  return String(message).replace(/[，,]?\s*如您有疑问[^。？]*是否提交审核？\s*/g, '').trim();
}

/** 审查申请提交成功提示：告知后续通知渠道与紧急对接人。 */
const REVIEW_SUBMITTED_MESSAGE = '系统已收到您的审查申请，请勿反复提交。审查通过后，将通过飞书/邮件通知您审查结果。如紧急项目可对接张卫明13671031283';

/** 风险等级文案：0 未识别风险、1 低风险、2 中风险、3 高风险、4 禁止交易。 */
const RISK_LEVEL_TEXTS = ['未识别风险', '低风险', '中风险', '高风险', '禁止交易'];

/** 校验类型中文名（命中明细分组标题）。 */
const VALIDATION_TYPE_LABELS = {
  SUBJECT: '主体校验',
  ITEM: '物项校验',
  COUNTRY: '禁运地校验',
  PURPOSE: '最终用途校验'
};

/** 命中明细分组件：按校验类型分组，组标题可点击折叠。 */
const ComplianceHitDetails = {
  name: 'ComplianceHitDetails',
  props: {
    groups: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      collapsed: {}
    };
  },
  methods: {
    toggleGroup (key) {
      this.$set(this.collapsed, key, !this.collapsed[key]);
    }
  },
  template: `
    <div class="compliance-hit-box">
      <div v-for="(group, groupIndex) in groups" :key="groupIndex" class="compliance-hit-group">
        <div class="compliance-hit-group__title" @click="toggleGroup(groupIndex)">
          <i :class="collapsed[groupIndex] ? 'el-icon-arrow-right' : 'el-icon-arrow-down'"></i>
          {{ group.label }}<template v-if="group.type">（{{ group.type }}）</template>
        </div>
        <div v-show="!collapsed[groupIndex]" class="compliance-hit-group__body">
          <div v-for="(item, itemIndex) in group.items" :key="itemIndex" class="compliance-hit-item">
            <div class="compliance-hit-item__field">• {{ item.field ? item.field + ': ' : '' }}{{ item.content }}</div>
            <div v-if="item.riskOutput" class="compliance-hit-item__risk">风险输出: {{ item.riskOutput }}</div>
          </div>
        </div>
      </div>
    </div>
  `
};

/**
 * 组装弹窗内容：风险等级（加粗）+ 整体结论 + 附加状态行 + 分组命中明细（带边框、可折叠）。
 */
function createMessageContent (vm, result, extraLines) {
  const children = [];
  const riskLevelLine = buildRiskLevelLine(result);
  if (riskLevelLine) {
    children.push(vm.$createElement('p', {
      style: { margin: '0 0 8px', fontWeight: '600', lineHeight: '20px' }
    }, riskLevelLine));
  }
  const conclusionLine = buildConclusionLine(result);
  if (conclusionLine) {
    children.push(vm.$createElement('p', { style: { margin: '0 0 8px', lineHeight: '20px' } }, conclusionLine));
  }
  (extraLines || []).filter(line => line && String(line).trim()).forEach(line => {
    children.push(vm.$createElement('p', { style: { margin: '0 0 8px', lineHeight: '20px' } }, line));
  });
  const groups = buildHitGroups(result);
  if (groups.length) {
    children.push(vm.$createElement(ComplianceHitDetails, { props: { groups } }));
  }
  return children.length ? vm.$createElement('div', children) : null;
}

/** 风险等级行：如「风险等级：禁止交易（4级）」。 */
function buildRiskLevelLine (result) {
  if (!result || result.overallRiskLevel == null) {
    return '';
  }
  const level = Number(result.overallRiskLevel);
  if (!Number.isInteger(level) || level < 0 || level >= RISK_LEVEL_TEXTS.length) {
    return '';
  }
  return `风险等级：${RISK_LEVEL_TEXTS[level]}（${level}级）`;
}

/** 整体结论行：优先平台总体输出（resultMsg），缺失时回退整体验证结论。 */
function buildConclusionLine (result) {
  const resultMsg = String((result && result.resultMsg) || '').trim();
  if (resultMsg) {
    return `整体结论：${stripSubmitReviewQuestion(resultMsg)}`;
  }
  const conclusion = String((result && result.overallConclusion) || '').trim();
  return conclusion ? stripSubmitReviewQuestion(conclusion) : '';
}

/** 命中明细分组：优先取 details 中风险等级大于 0 的条目，缺失时回退 detailMsg 拆行。 */
function buildHitGroups (result) {
  const groups = [];
  const groupIndex = {};
  ((result && result.details) || []).forEach(detail => {
    if (!isHitDetail(detail)) {
      return;
    }
    const type = detail.validationType || '';
    if (!groupIndex[type]) {
      groupIndex[type] = { type, label: VALIDATION_TYPE_LABELS[type] || '命中明细', items: [] };
      groups.push(groupIndex[type]);
    }
    groupIndex[type].items.push({
      field: detail.validatedItem || '',
      content: detail.validatedContent || detail.hitDetails || '',
      riskOutput: detail.riskOutput || detail.hitDetails || ''
    });
  });
  if (groups.length) {
    return groups;
  }
  const fallbackLines = splitHitLines(result && result.detailMsg);
  if (!fallbackLines.length) {
    return [];
  }
  return [{
    type: '',
    label: '命中明细',
    items: fallbackLines.map(line => ({ field: '', content: line, riskOutput: '' }))
  }];
}

/** 命中条目判定：风险等级大于 0 且命中文案有效。 */
function isHitDetail (detail) {
  if (!detail) {
    return false;
  }
  const content = String(detail.riskOutput || detail.hitDetails || '').trim();
  return Number(detail.riskLevel) > 0 && Boolean(content) && !content.includes('未识别到风险');
}

/** detailMsg 兜底拆行：平台聚合文案通常用顿号/分号/换行拼接多条命中，逐条列出一行。 */
function splitHitLines (message) {
  if (!message) {
    return [];
  }
  return String(message)
    .split(/[、；;\r\n]+/)
    .map(line => line.trim())
    .filter(Boolean);
}

/** 仅国内合同使用本次新增的合规检测接口。 */
export function isDomesticContract (contract) {
  const internationalTypes = ['国际业务下单', '国际分销下单', '国际备件下单'];
  return !internationalTypes.includes(contract.contractType) &&
    !(contract.contractType === '服务订单' && contract.civilInternational === 1);
}

/** 根据最终提交数据构造国内合同合规检测参数。 */
export function buildContractComplianceRequest (form, submitData, productList, address) {
  const contractInfo = submitData.contractInfo || {};
  const contractDetail = submitData.contractDetail || {};
  const products = productList || [];
  const selectedAddress = address || {};
  return {
    businessScene: 'ORDER_CONTRACT_SAVE',
    contractStatus: contractInfo.contractStatus == null ? form.contractStatus : contractInfo.contractStatus,
    contractProcessStatus: contractInfo.contractProcessStatus == null
      ? form.contractProcessStatus
      : contractInfo.contractProcessStatus,
    originalBizId: contractInfo.id ? String(contractInfo.id) : null,
    subjectNameList: distinctValues([
      form.supplyUnit,
      contractDetail.orderUnit,
      contractDetail.finalCustomer,
      contractDetail.integratorName,
      contractDetail.metrologyIntegratorName,
      contractDetail.finalIntegratorsName,
      contractDetail.productServiceProviderA,
      contractDetail.productServiceProviderB,
      contractDetail.productServiceProviderC,
      contractDetail.serviceSubjectName,
      selectedAddress.receiverUnit
    ]),
    addressList: distinctValues([normalizeAddress(selectedAddress)]),
    itemModelList: distinctValues(products.map(item => item.productName)),
    itemPartNumberList: distinctValues(products.map(item => item.materialNumberId || item.productCode)),
    projectNum: contractDetail.projectCode,
    projectName: contractInfo.projectName,
    projectDescription: form.frameworkAgreementName
  };
}

/** 组装国际合同订单员节点（发布）合规检测参数。 */
export function buildIntlOrderPublishComplianceRequest (form, productList) {
  return {
    businessScene: 'INTL_ORDER_PUBLISH',
    originalBizId: form.contractNum || null,
    subjectNameList: pickValues([form.orderUnit, form.finalCustomer, form.secondChannelName]),
    itemModelList: pickValues((productList || []).map(item => item.productName)),
    itemPartNumberList: pickValues((productList || []).map(item => item.materialNumberId || item.productCode)),
    projectName: form.projectName,
    projectNum: form.projectCode,
    countryStateNameList: buildCountryStateNameList(form),
    addressList: pickValues([form.destination])
  };
}

/** 组装国际合同商务节点（审批）合规检测参数。 */
export function buildIntlBusinessApprovalComplianceRequest (form, productList) {
  return {
    businessScene: 'INTL_BUSINESS_APPROVAL',
    originalBizId: form.contractNum || null,
    subjectNameList: pickValues([form.crmAccountName, form.orderUnit, form.finalCustomer, form.secondChannelName]),
    itemModelList: pickValues((productList || []).map(item => item.productName)),
    projectName: form.projectName,
    countryStateNameList: buildCountryStateNameList(form),
    addressList: pickValues([form.destination])
  };
}

/** 组装国际电汇单（商务录入）合规检测参数：客户名称、实际付款人、实际付款人国家。 */
export function buildIntlTelegraphicTransferComplianceRequest (info) {
  const request = {
    businessScene: 'INTL_TELEGRAPHIC_TRANSFER_BUSINESS',
    originalBizId: (info && (info.telegraphicTransferNum || info.telegraphicTransferNo)) || null,
    subjectNameList: pickValues([info && info.orderUnit, info && info.drawee])
  };
  // 实际付款人国家：未填写时按旧规则默认中国
  const countryName = (info && info.sourceCountries) || '中国';
  request.countryStateNameList = [{ countryName }];
  return request;
}

/** 组装提交分批发货合规检测参数：客户名称、公司名称、产品型号、销往国家、公司地址。 */
export function buildIntlPartialShipmentComplianceRequest ({ agentName, soldCountry, corporateName, corporateAddress, deliveryNum, products }) {
  return {
    businessScene: 'INTL_PARTIAL_SHIPMENT_SUBMIT',
    originalBizId: deliveryNum || null,
    subjectNameList: pickValues([agentName, corporateName]),
    itemModelList: pickValues((products || []).map(item => item.productName)),
    countryStateNameList: pickValues([soldCountry]).map(name => ({ countryName: name })),
    addressList: pickValues([corporateAddress])
  };
}

/** 国际场景：区域、销往区域两个字段均写入对象数组。 */
function buildCountryStateNameList (form) {
  return pickValues([form.regionName, form.soldCountry]).map(name => ({ countryName: name }));
}

/** 去除空值并保持传入顺序原样（国际产品明细按顺序原样传入，不做去重）。 */
function pickValues (values) {
  return (values || []).filter(value => value && String(value).trim());
}

/** 去除空值和重复值，保持检测请求稳定。 */
function distinctValues (values) {
  return Array.from(new Set(values.filter(value => value && String(value).trim())));
}

/** 审查申请提交成功提示：优先展示平台返回的 promptMessage，缺失时使用兜底文案。 */
function notifyReviewSubmitted (vm, reviewData) {
  const message = (reviewData && reviewData.promptMessage) || REVIEW_SUBMITTED_MESSAGE;
  vm.$message({
    type: 'success',
    message,
    duration: 6000,
    showClose: true
  });
}

/** 将 Element UI 的取消异常转换为明确的布尔选择。 */
async function confirmReviewCreation (vm, message, type = 'warning') {
  try {
    await vm.$confirm(message || '检测到出口合规风险，是否创建审查单？', '出口合规提示', {
      confirmButtonText: '创建审查单',
      cancelButtonText: '暂不创建',
      type,
      closeOnClickModal: false,
      customClass: 'compliance-message-box'
    });
    return true;
  } catch (error) {
    return false;
  }
}

/** 与原国内检测规则一致，详细地址中移除已单独维护的省、市、县。 */
function normalizeAddress (address) {
  if (!address.receiptAddress) {
    return '';
  }
  return [address.province, address.city, address.county]
    .filter(Boolean)
    .reduce((value, region) => value.split(region).join(''), address.receiptAddress);
}

/** 从前端已有查询接口加载原合同数据，构造生成新合同的检测请求。 */
export async function buildCreateContractComplianceRequest (row) {
  const detailResponse = await getContractDetailAllInfoByConNum(row.contractNum);
  const detailBody = detailResponse.data || {};
  if (detailBody.status !== '200' || !detailBody.data) {
    throw new Error('生成新合同的合规检测数据加载失败');
  }
  const form = detailBody.data || {};
  const responses = await Promise.all([
    getContractProductInfoByContractId(form.id, form.contractType, form.asInterDirect),
    getReceiptAddressByContractId(form.id)
  ]);
  const productBody = responses[0].data || {};
  const addressBody = responses[1].data || {};
  if (productBody.status !== '200' || addressBody.status !== '200') {
    throw new Error('生成新合同的合规检测数据加载失败');
  }
  const submitData = {
    contractInfo: {
      id: form.id,
      projectName: form.projectName,
      contractStatus: form.contractStatus,
      contractProcessStatus: form.contractProcessStatus
    },
    contractDetail: form
  };
  return {
    ...buildContractComplianceRequest(form, submitData, productBody.data || [], addressBody.data || {}),
    businessScene: 'ORDER_CONTRACT_CREATE',
    originalBizId: String(form.id)
  };
}
